import type { BarcodeFormat } from '@erp/shared';
import JsBarcode from 'jsbarcode';
import { toast } from 'sonner';

const JSBARCODE_FORMAT: Record<Exclude<BarcodeFormat, 'qr'>, string> = {
  code128: 'CODE128',
  ean13: 'EAN13',
  ean8: 'EAN8',
  upc_a: 'UPC',
};

/**
 * Opens a tiny popup window containing one barcode label sized for a
 * typical 40×25mm thermal sticker (the most common retail label),
 * with a print-only stylesheet and an auto-fired `window.print()`.
 *
 * Browser-native print stays the simplest path for "one label now":
 *   * no server round-trip, no PDF dependency
 *   * the operator's browser already knows their printer
 *   * Avery-template multi-label sheets are out of scope (Slice 7 in
 *     the ultrathink plan; needs `bwip-js` + per-template layout)
 *
 * The SVG is generated client-side via jsbarcode. QR is currently
 * unsupported (jsbarcode is 1D-only); we surface a translated warning
 * instead of opening a blank window.
 */
export function printItemBarcode(opts: {
  sku: string;
  name: string;
  barcode: string;
  format: BarcodeFormat;
  /** Translated "No barcode to print" label used when value/format aren't printable. */
  errorLabel: string;
}): void {
  const { sku, name, barcode, format, errorLabel } = opts;
  if (!barcode.trim() || format === 'qr') {
    toast.error(errorLabel);
    return;
  }

  // Render the SVG in a detached document we control, then serialize it
  // into the popup. Doing it in-place would race against the new
  // window's <svg> ref being mounted.
  const ownerDoc = document.implementation.createHTMLDocument('barcode');
  const svg = ownerDoc.createElementNS('http://www.w3.org/2000/svg', 'svg');
  try {
    JsBarcode(svg, barcode.trim(), {
      format: JSBARCODE_FORMAT[format],
      displayValue: true,
      fontSize: 14,
      height: 60,
      margin: 4,
    });
  } catch (e) {
    toast.error((e as Error).message);
    return;
  }
  const svgHtml = new XMLSerializer().serializeToString(svg);

  const win = window.open('', '_blank', 'width=480,height=320');
  if (!win) {
    // Popup blocked. Falling back to a confirm + same-tab print would
    // hijack the operator's session, so we just surface the failure.
    toast.error(errorLabel);
    return;
  }
  // Minimal label HTML. The @page rule sizes for a common 40×25mm
  // thermal sticker; the @media print block hides everything except
  // the label so the print dialog gets a clean preview.
  win.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(sku)} — ${escapeHtml(name)}</title>
  <style>
    @page { size: 40mm 25mm; margin: 0; }
    body { margin: 0; font-family: system-ui, sans-serif; }
    .label {
      width: 40mm; height: 25mm;
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; box-sizing: border-box; padding: 1mm;
    }
    .label .name { font-size: 7pt; line-height: 1.1; text-align: center; max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
    .label svg { max-width: 38mm; max-height: 15mm; }
    @media screen {
      body { background: #f4f4f5; padding: 24px; }
      .label { background: white; box-shadow: 0 1px 4px rgba(0,0,0,.1); }
    }
  </style>
</head>
<body>
  <div class="label">
    <div class="name">${escapeHtml(name)}</div>
    ${svgHtml}
  </div>
  <script>window.addEventListener('load', () => { window.print(); });</script>
</body>
</html>`);
  win.document.close();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
